import { createSlice } from '@reduxjs/toolkit';
import { RootState } from '~/utils/redux/store';
import { StorageEnum } from '~/utils/enum/base';
import { getProfile, handleLogin } from './authThunk';
import { IAccount, IAccountPermission } from '~/utils/interface/auth';

export interface AuthState {
  accessToken?: string;
  user?: IAccount;
  permission?: IAccountPermission;
  // the admin pages a staff account's role grants (undefined for everyone else)
  staffFeatures?: string[];
}

const getStoredUser = (): IAccount | undefined => {
  const raw = localStorage.getItem(StorageEnum.USER);
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as IAccount;
  } catch {
    return undefined;
  }
};

const initialState: AuthState = {
  accessToken: localStorage.getItem(StorageEnum.ACCESS_TOKEN) || undefined,
  user: getStoredUser(),
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    handleLogout(state) {
      localStorage.removeItem(StorageEnum.ACCESS_TOKEN);
      localStorage.removeItem(StorageEnum.REFRESH_TOKEN);
      localStorage.removeItem(StorageEnum.USER);
      state.accessToken = undefined;
      state.user = undefined;
      state.permission = undefined;
      state.staffFeatures = undefined;
    },
    setAccessToken(state, action) {
      state.accessToken = action.payload;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(handleLogin.pending, (state) => {})
      .addCase(handleLogin.fulfilled, (state, action) => {
        const { account, token } = action.payload.data;
        state.accessToken = token.access;
        state.user = account;
        localStorage.setItem(StorageEnum.ACCESS_TOKEN, token.access);
        localStorage.setItem(StorageEnum.REFRESH_TOKEN, token.refresh);
        localStorage.setItem(StorageEnum.USER, JSON.stringify(account));
      })
      .addCase(handleLogin.rejected, (state) => {})
      .addCase(getProfile.fulfilled, (state, action) => {
        state.user = action.payload.profile;
        state.permission = action.payload.permission;
        state.staffFeatures = action.payload.staffFeatures;
        localStorage.setItem(StorageEnum.USER, JSON.stringify(action.payload.profile));
      });
  },
});

export const authActions = authSlice.actions;

export const selectUser = (state: RootState) => state.auth.user;
export const selectStaffFeatures = (state: RootState) => state.auth.staffFeatures;
export const selectPermission = (state: RootState) => state.auth.permission;
export const selectAccessToken = (state: RootState) => state.auth.accessToken;

const authReducer = authSlice.reducer;
export default authReducer;
